import {
    getLanguageLabel,
    getPlatformIconMeta,
    getPlatformLabel,
    normalizeHashtagList,
} from "./campaignPayload";
import { normalizePost } from "./normalizePost";
import { normalizeOpportunity } from "./normalizeOpportunity";
import { normalizeOpportunitiesList } from "./opportunitySelection";

function unwrapCampaign(data) {
    if (data?.campaign) return data.campaign;
    return data ?? {};
}

function normalizeIdeas(raw) {
    const selectedIdeas = raw.selected_ideas ?? raw.selectedIdeas;
    if (Array.isArray(selectedIdeas) && selectedIdeas.length > 0) {
        return selectedIdeas;
    }

    const ideaSelection = raw.ideaSelection ?? raw.idea_selection;
    if (!Array.isArray(ideaSelection)) return [];

    const selected = ideaSelection.filter((idea) => idea.selected);
    return selected.length > 0 ? selected : ideaSelection;
}

function normalizePosts(raw) {
    const posts = raw.posts ?? raw.postSelection ?? raw.post_selection;
    if (!Array.isArray(posts)) return [];

    return posts.map((post) => normalizePost(post));
}

function normalizeFollowUp(raw) {
    const followUp = raw.followUp ?? raw.follow_up ?? {};

    const rawOpportunities = followUp.opportunities ?? followUp.opportunity ?? null;

    return {
        tone: followUp.tone ?? null,
        topic: followUp.topic ?? null,
        target: followUp.target ?? null,
        hashtags: normalizeHashtagList(followUp.hashtags),
        searchedQueries: followUp.searchedQueries ?? followUp.searched_queries ?? null,
        opportunities: normalizeOpportunitiesList(rawOpportunities).map(normalizeOpportunity),
    };
}

function normalizeBasicDetails(raw) {
    const basicDetails = raw.basicDetails ?? raw.basic_details ?? {};

    return {
        name: basicDetails.name ?? raw.name ?? null,
        platform: basicDetails.platform ?? raw.platform ?? null,
        language: basicDetails.language ?? raw.language ?? null,
        preferences: basicDetails.preferences ?? [],
    };
}

export function normalizeCampaign(data) {
    const raw = unwrapCampaign(data);
    const basicDetails = normalizeBasicDetails(raw);
    const followUp = normalizeFollowUp(raw);
    const selectedIdeas = normalizeIdeas(raw);
    const posts = normalizePosts(raw);

    return {
        id: raw.id ?? null,
        userId: raw.user_id ?? raw.userId ?? null,
        createdAt: raw.created_at ?? raw.createdAt ?? null,
        name: basicDetails.name || "Untitled campaign",
        platform: basicDetails.platform,
        language: basicDetails.language,
        status: raw.status ?? null,
        basicDetails,
        followUp,
        selectedIdeas,
        posts,
        ideasCount: raw.ideas_count ?? raw.ideasCount ?? selectedIdeas.length,
        postsCount: raw.posts_count ?? raw.postsCount ?? posts.length,
    };
}

export function normalizeCampaignsResponse(data) {
    let campaigns = [];
    let total = 0;
    let totalPages;
    let hasServerPagination = false;
    let hasCursorPagination = false;
    let hasMore = false;
    let lastDocId = null;

    if (Array.isArray(data)) {
        campaigns = data;
        total = data.length;
    } else if (Array.isArray(data?.campaigns)) {
        campaigns = data.campaigns;
        total = data.total ?? data.count ?? data.campaigns.length;
        totalPages = data.total_pages ?? data.totalPages;
        hasServerPagination =
            data.total != null || data.total_pages != null || data.totalPages != null;
    } else if (Array.isArray(data?.data)) {
        campaigns = data.data;
        hasCursorPagination = data.has_more != null || data.last_doc_id != null;
        hasMore = Boolean(data.has_more);
        lastDocId = data.last_doc_id ?? null;
        total = data.total ?? data.count ?? data.data.length;
        totalPages = data.total_pages ?? data.totalPages;
        hasServerPagination = hasCursorPagination;
    }

    return {
        campaigns: campaigns.map(normalizeCampaign),
        total,
        totalPages,
        hasServerPagination,
        hasCursorPagination,
        hasMore,
        lastDocId,
    };
}

export function getCampaignName(campaign) {
    return campaign?.name || campaign?.basicDetails?.name || "Untitled campaign";
}

export function getCampaignDate(campaign) {
    return campaign?.createdAt ?? campaign?.created_at ?? campaign?.created ?? null;
}

export function getDisplayPlatform(campaign) {
    const value = campaign?.platform ?? campaign?.basicDetails?.platform;
    return value ? getPlatformLabel(value) : null;
}

export function getDisplayLanguage(campaign) {
    const value = campaign?.language ?? campaign?.basicDetails?.language;
    return value ? getLanguageLabel(value) : null;
}

export function getCampaignPlatformIcon(campaign) {
    const value = campaign?.platform ?? campaign?.basicDetails?.platform;
    return getPlatformIconMeta(value);
}

export function isCampaignDraft(campaign) {
    return String(campaign?.status ?? "").toLowerCase() === "draft";
}
